import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  Switch,
  Chip,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, gql } from '@apollo/client';
import { ConfirmDialog } from '@personal-dashboard/ui';
import { useAuth } from '../../../context/AuthContext';
import { CreateUserDialog } from './CreateUserDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';

const GET_USERS = gql`
  query GetUsers {
    getUsers {
      id
      username
      email
      role
      isActive
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      username
      email
      role
      isActive
    }
  }
`;

const SET_USER_PASSWORD = gql`
  mutation SetUserPassword($id: ID!, $password: String!) {
    setUserPassword(id: $id, password: $password)
  }
`;

const SET_USER_ACTIVE = gql`
  mutation SetUserActive($id: ID!, $active: Boolean!) {
    setUserActive(id: $id, active: $active) {
      id
      isActive
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

export const UsersTab: React.FC = () => {
  const { user: me } = useAuth();
  const { data, loading, error, refetch } = useQuery(GET_USERS);

  const [createUser] = useMutation(CREATE_USER, { refetchQueries: [{ query: GET_USERS }] });
  const [setUserPassword] = useMutation(SET_USER_PASSWORD);
  const [setUserActive] = useMutation(SET_USER_ACTIVE, { refetchQueries: [{ query: GET_USERS }] });
  const [deleteUser] = useMutation(DELETE_USER, { refetchQueries: [{ query: GET_USERS }] });

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [resetting, setResetting] = useState<any>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<any>(null);

  const users = data?.getUsers || [];

  const handleCreate = async (username: string, email: string, password: string) => {
    setCreateError(null);
    try {
      await createUser({ variables: { input: { username, email, password } } });
      setCreateOpen(false);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create user');
    }
  };

  const handleResetPassword = async (password: string) => {
    if (!resetting) return;
    setResetError(null);
    try {
      await setUserPassword({ variables: { id: resetting.id, password } });
      setResetting(null);
    } catch (err: any) {
      setResetError(err.message || 'Failed to set password');
    }
  };

  const handleToggleActive = async (u: any, active: boolean) => {
    try {
      await setUserActive({ variables: { id: u.id, active } });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteUser({ variables: { id: deleting.id } });
      setDeleting(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <Alert severity="info">Loading users…</Alert>;
  if (error) return <Alert severity="error">Failed to load users: {error.message}</Alert>;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Users ({users.length})
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New User
        </Button>
      </Stack>

      <CreateUserDialog
        open={createOpen}
        error={createError}
        onCancel={() => { setCreateOpen(false); setCreateError(null); }}
        onCreate={handleCreate}
      />

      <ResetPasswordDialog
        open={!!resetting}
        username={resetting?.username || ''}
        error={resetError}
        onCancel={() => { setResetting(null); setResetError(null); }}
        onConfirm={handleResetPassword}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Delete user?"
        message={`This will permanently delete "${deleting?.username ?? ''}" and their data.`}
        confirmLabel="Delete"
        severity="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />

      <Stack spacing={1}>
        {users.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No users yet.
          </Typography>
        )}
        {users.map((u: any) => {
          const isSelf = u.id === me?.id;
          return (
            <Paper key={u.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flexGrow: 1, minWidth: 180 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {u.username}
                    {isSelf && <Chip label="you" size="small" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {u.email || 'no email'} · {u.role} {!u.isActive && '· disabled'}
                  </Typography>
                </Box>

                <Switch
                  checked={u.isActive}
                  disabled={isSelf}
                  onChange={(e) => handleToggleActive(u, e.target.checked)}
                  title={isSelf ? 'You cannot disable your own account' : 'Disable/enable login'}
                />

                <IconButton size="small" onClick={() => setResetting(u)} aria-label="Reset password">
                  <LockResetIcon fontSize="small" />
                </IconButton>

                {!isSelf && (
                  <IconButton size="small" color="error" onClick={() => setDeleting(u)} aria-label="Delete user">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default UsersTab;
